import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiEdit3, FiRefreshCw, FiSave, FiSearch, FiX } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useAuth } from 'context/AuthProvider';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { showToast } from 'utils/helpers';
import {
  getEmployeeMonthlyIncomeReport,
  updateEmployeeMonthlyIncome,
} from 'services/retail-report.service';
import './employeeMonthlyIncome.scss';

const MONEY_FIELDS = [
  {
    key: 'setup_income',
    apiKey: 'setupincome',
    formApiKey: 'manualsetupincome',
    label: 'Dịch vụ setup / loa RCF / phòng hát / GD VIP',
    formLabel: 'Điều chỉnh thêm cho dịch vụ setup',
    short: 'Dịch vụ setup',
  },
  { key: 'project_income', apiKey: 'projectincome', label: 'Tip ITC / công trình / ánh sáng', short: 'ITC / công trình' },
  { key: 'promoted_product_income', apiKey: 'promotedproductincome', label: 'Tive sản phẩm đẩy quảng cáo', short: 'SP đẩy quảng cáo' },
  { key: 'warranty_income', apiKey: 'warrantyincome', label: 'Tip bảo hành', short: 'Bảo hành' },
  { key: 'dinner_allowance', apiKey: 'dinnerallowance', label: 'Tiền ăn tối', short: 'Ăn tối' },
  { key: 'long_distance_allowance', apiKey: 'longdistanceallowance', label: 'Kỹ thuật giao hàng xe máy trên 30 km', short: 'Đi xa > 30 km' },
  { key: 'setup_return_income', apiKey: 'setupreturnincome', label: 'Tive setup lại', short: 'Setup lại' },
  { key: 'return_goods_income', apiKey: 'returngoodsincome', label: 'Tive trả hàng', short: 'Trả hàng' },
  { key: 'priority_product_income', apiKey: 'priorityproductincome', label: 'Tip sản phẩm ưu tiên', short: 'SP ưu tiên' },
];

const formatNumber = (value) => new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Number(value) || 0);
const formatMoney = (value, emptyZero = false) => {
  const amount = Number(value) || 0;
  if (emptyZero && amount === 0) return '—';
  return `${formatNumber(amount)} đ`;
};
const formatVatMoney = (value, emptyZero = false) => {
  const amount = Number(value) || 0;
  if (emptyZero && amount === 0) return '—';
  return `${new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(amount)} đ`;
};
const parseMoney = (value) => Number(`${value || ''}`.replace(/[^0-9]/g, '')) || 0;
const currentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};
const emptyReport = () => ({ summary: {}, staff: [], stores: [], orders: [] });
const filenameScope = (value) => `${value || ''}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[đĐ]/g, 'd').replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 100);
const rowIdentity = (row) => `${row.companyid}:${row.storeid}:${row.username}`;
const sameEmployeeStore = (order, row) => `${order.companyid}` === `${row.companyid}`
  && `${order.storeid}` === `${row.storeid}` && `${order.username}` === `${row.username}`;
const isReturnEvent = (order) => order.eventtype === 'RETURN';
const isVatInvoiceEvent = (order) => order.eventtype === 'VAT_INVOICE';
const eventLabel = (order) => isVatInvoiceEvent(order) ? 'Hóa đơn VAT' : isReturnEvent(order) ? 'Thu nhập bị thu hồi' : 'Thu nhập từ đơn';
const missingConvertedCommission = (order) => !isVatInvoiceEvent(order) && !isReturnEvent(order)
  && Number(order.isconvertedpreorder) === 1 && Number(order.hascommissionplan) === 0;
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('vi-VN').format(date);
};
const shareLabel = (order) => {
  if (Number(order.issalesperson) === 0) return 'Không phân bổ doanh số';
  if (!(Number(order.salespersoncount) > 0)) return '—';
  const percent = order.allocationpercent ?? (100 / Number(order.salespersoncount));
  return `1/${Number(order.salespersoncount)} (${new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(percent)}%)`;
};

const buildFormState = (row) => {
  const source = row || {};
  return MONEY_FIELDS.reduce(
    (result, field) => ({ ...result, [field.key]: formatNumber(source[field.formApiKey || field.apiKey]) }),
    { note: source.note || '' },
  );
};

const EmployeeIncomeModal = ({ row, open, saving, onClose, onSave }) => {
  const [formData, setFormData] = useState(() => buildFormState(row));

  useEffect(() => setFormData(buildFormState(row)), [row]);
  if (!open || !row) return null;

  return (
    <div className='bc_emi_modal_backdrop' role='presentation' onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className='bc_emi_modal' role='dialog' aria-modal='true' aria-labelledby='bc-emi-modal-title'>
        <header className='bc_emi_modal_header'>
          <div>
            <span>Điều chỉnh thu nhập tháng</span>
            <h3 id='bc-emi-modal-title'>{row.username} - {row.fullname}</h3>
            <p>{row.storename}</p>
          </div>
          <button type='button' className='bc_emi_icon_button' title='Đóng' aria-label='Đóng' onClick={onClose}>
            <FiX />
          </button>
        </header>

        <div className='bc_emi_auto_values'>
          <div><span>Doanh thu VAT (gồm VAT)</span><strong>{formatVatMoney(row.salesrevenue)}</strong></div>
          <div><span>Hoa hồng theo doanh số</span><strong>{formatMoney(row.salescommission)}</strong></div>
          <div><span>Tip theo khai báo</span><strong>{formatMoney(row.tipincome)}</strong></div>
          <div><span>Hoa hồng giao hàng</span><strong>{formatMoney(row.deliverycommission)}</strong></div>
          <div><span>Thu nhập setup theo khai báo</span><strong>{formatMoney((Number(row.setupincome) || 0) - (Number(row.manualsetupincome) || 0))}</strong></div>
        </div>

        <div className='bc_emi_modal_body'>
          <p className='bc_emi_form_note'>Chỉ nhập phần điều chỉnh ngoài đơn hàng. Hoa hồng theo doanh số chia đều cho nhân viên bán hàng trên đơn; tip và thu nhập setup lấy đúng người nhận khai báo.</p>
          <div className='bc_emi_form_grid'>
            {MONEY_FIELDS.map((field) => (
              <label key={field.key} className='bc_emi_field'>
                <span>{field.formLabel || field.label}</span>
                <div className='bc_emi_money_input'>
                  <input
                    inputMode='numeric'
                    aria-label={field.formLabel || field.label}
                    value={formData[field.key]}
                    onChange={(event) => setFormData((current) => ({
                      ...current,
                      [field.key]: formatNumber(parseMoney(event.target.value)),
                    }))}
                  />
                  <em>đ</em>
                </div>
              </label>
            ))}
          </div>
          <label className='bc_emi_field bc_emi_note_field'>
            <span>Ghi chú</span>
            <textarea
              rows='3'
              maxLength='1000'
              placeholder='Nội dung đối soát hoặc giải trình khoản phát sinh'
              value={formData.note}
              onChange={(event) => setFormData((current) => ({ ...current, note: event.target.value }))}
            />
          </label>
        </div>

        <footer className='bc_emi_modal_footer'>
          <button type='button' className='bc_emi_button is-secondary' onClick={onClose}><FiX /> Đóng</button>
          <button type='button' className='bc_emi_button is-primary' disabled={saving} onClick={() => onSave(formData)}>
            {saving ? <FiRefreshCw className='is-spinning' /> : <FiSave />} Lưu điều chỉnh
          </button>
        </footer>
      </section>
    </div>
  );
};

const EmployeeOrdersModal = ({ row, orders, onClose }) => {
  if (!row) return null;

  return (
    <div className='bc_emi_modal_backdrop' role='presentation' onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className='bc_emi_modal bc_emi_orders_modal' role='dialog' aria-modal='true' aria-labelledby='bc-emi-orders-title'>
        <header className='bc_emi_modal_header'>
          <div>
            <span>Đơn bán và trả hàng trong tháng</span>
            <h3 id='bc-emi-orders-title'>{row.username} - {row.fullname}</h3>
            <p>{row.storename}</p>
          </div>
          <button type='button' className='bc_emi_icon_button' title='Đóng đơn hàng' aria-label='Đóng đơn hàng' onClick={onClose}><FiX /></button>
        </header>
        <div className='bc_emi_modal_body'>
          <p className='bc_emi_form_note'>Doanh thu gồm VAT lấy đúng tổng tiền hóa đơn Viettel đã phát hành, theo ngày hóa đơn; điều chỉnh giảm có số âm. Doanh thu chia theo cửa hàng xuất và nhân viên bán hàng. Hoa hồng, tip, setup và thu hồi thu nhập giữ theo nghiệp vụ đã ghi nhận trên đơn.</p>
          <div className='bc_emi_orders_table_wrap'>
            <table className='bc_emi_orders_table'>
              <thead><tr><th>Mã đơn</th><th>Hóa đơn VAT</th><th>Ngày hóa đơn / ngày đơn</th><th>Nghiệp vụ / ngày ghi nhận</th><th>Cửa hàng xuất</th><th>Nhân viên cùng bán</th><th>Tiền hóa đơn gồm VAT</th><th>Doanh thu VAT tại cửa hàng</th><th>Tỷ lệ chia</th><th>Doanh thu VAT nhân viên</th><th>Hoa hồng theo doanh số</th><th>Tip theo khai báo</th><th>Giao hàng</th><th>Thu nhập setup</th></tr></thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={`${order.eventkey || `${order.eventtype || 'SALE'}:${order.orderid}`}:${order.storeid}:${order.username}`}>
                    <td><Link to={`/orders/edit/${order.orderid}?tab_active=information`}>{order.ordercode}</Link>{missingConvertedCommission(order) ? <div>Chưa lưu hoa hồng</div> : null}</td>
                    <td>{order.invoiceno || '—'}</td><td>{formatDate(order.invoiceissueddate || order.orderdate)}</td>
                    <td><strong>{eventLabel(order)}</strong>{isReturnEvent(order) && order.returncode ? <div>{order.returncode}</div> : null}<div>{formatDate(order.eventdate || order.orderdate)}</div></td>
                    <td>{order.storename}</td><td>{order.sellernames || '—'}</td>
                    <td className='is-money'>{formatVatMoney(isVatInvoiceEvent(order) ? order.orderrevenue : 0)}</td>
                    <td className='is-money'>{formatVatMoney(isVatInvoiceEvent(order) ? (order.storeorderrevenue ?? order.orderrevenue) : 0)}</td><td>{shareLabel(order)}</td>
                    <td className='is-money is-revenue'>{formatVatMoney(order.salesrevenue)}</td>
                    <td className='is-money'>{formatMoney(order.salescommission, true)}</td>
                    <td className='is-money'>{formatMoney(order.tipincome, true)}</td>
                    <td className='is-money'>{formatMoney(order.deliverycommission, true)}</td>
                    <td className='is-money'>{formatMoney(order.setupincome, true)}</td>
                  </tr>
                ))}
                {!orders.length ? <tr><td colSpan='14' className='bc_emi_empty'>Không có đơn bán hoặc trả hàng trong kỳ.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
        <footer className='bc_emi_modal_footer'><button type='button' className='bc_emi_button is-secondary' onClick={onClose}><FiX /> Đóng</button></footer>
      </section>
    </div>
  );
};

const EmployeeMonthlyIncomePage = () => {
  const { user } = useAuth();
  const canOpenReport = (alias) => Number(user?.isAdministrator) === 1 || (user?.functions || []).some((permission) => String(permission).trim().toUpperCase() === alias);
  const [portalScope, setPortalScope] = useState(() => getPortalScope());
  const [reportMonth, setReportMonth] = useState(currentMonth);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [report, setReport] = useState(() => ({ ...emptyReport(), context: null }));
  const [scopeOptions, setScopeOptions] = useState({ companyId: '', stores: [] });
  const [scopeFilter, setScopeFilter] = useState({ companyId: '', businessId: '', storeId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [ordersRow, setOrdersRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const requestSequence = useRef(0);
  const companyId = `${portalScope.company_id || ''}`;
  const businessId = scopeFilter.companyId === companyId ? scopeFilter.businessId : '';
  const storeId = scopeFilter.companyId === companyId ? scopeFilter.storeId : '';
  const reportContext = JSON.stringify([companyId, businessId, storeId, reportMonth, keyword]);
  const activeReportContext = useRef(reportContext);
  activeReportContext.current = reportContext;
  const hasCurrentReport = report.context === reportContext;
  const data = hasCurrentReport ? report : emptyReport();
  const authorizedStores = useMemo(() => scopeOptions.companyId === companyId ? scopeOptions.stores : [], [companyId, scopeOptions]);
  const businessOptions = useMemo(() => [...new Map(authorizedStores.filter((row) => row.businessid)
    .map((row) => [`${row.businessid}`, row])).values()], [authorizedStores]);
  const storeOptions = useMemo(() => authorizedStores.filter((row) => !businessId || `${row.businessid}` === businessId), [authorizedStores, businessId]);
  const selectedBusiness = businessOptions.find((row) => `${row.businessid}` === businessId);
  const selectedStore = authorizedStores.find((row) => `${row.storeid}` === storeId);
  const scopeLabel = selectedStore ? `Cửa hàng ${selectedStore.storename}`
    : selectedBusiness ? `Chi nhánh ${selectedBusiness.businessname}` : 'Toàn bộ cửa hàng được phân quyền trong công ty';
  const canExport = !loading && !error && hasCurrentReport && data.staff.length > 0;

  const functionSet = useMemo(
    () => new Set((user?.functions || []).map((item) => `${item || ''}`.trim().toUpperCase())),
    [user?.functions],
  );
  const canEdit = Number(user?.isAdministrator) === 1
    || `${user?.user_name || ''}`.toLowerCase() === 'administrator'
    || functionSet.has('SL_RETAIL_EMPLOYEE_MONTHLY_INCOME_EDIT');

  useEffect(() => {
    const handleScopeChange = (event) => setPortalScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handleScopeChange);
  }, []);

  useEffect(() => {
    setScopeFilter({ companyId, businessId: '', storeId: '' });
    setScopeOptions({ companyId, stores: [] });
  }, [companyId]);

  useEffect(() => {
    setEditingRow(null);
    setOrdersRow(null);
    setReport({ ...emptyReport(), context: null });
    setError('');
    requestSequence.current += 1;
  }, [reportContext]);

  const loadReport = useCallback(async () => {
    if (reportContext !== activeReportContext.current) return;
    const sequence = ++requestSequence.current;
    const isCurrentRequest = () => sequence === requestSequence.current && reportContext === activeReportContext.current;
    if (!companyId) {
      setReport({ ...emptyReport(), context: null });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setReport({ ...emptyReport(), context: null });
    try {
      const result = await getEmployeeMonthlyIncomeReport({
        company_id: companyId, report_month: reportMonth, keyword,
        ...(businessId ? { business_id: businessId } : {}),
        ...(storeId ? { store_id: storeId } : {}),
      });
      if (!isCurrentRequest()) return;
      setReport({ context: reportContext, summary: result?.summary || {}, staff: result?.staff || [], stores: result?.stores || [], orders: result?.orders || [] });
      setScopeOptions({ companyId, stores: result?.stores || [] });
    } catch (requestError) {
      if (isCurrentRequest()) {
        setReport({ ...emptyReport(), context: null });
        setError(requestError?.message || 'Không thể tải báo cáo thu nhập nhân viên.');
      }
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  }, [businessId, companyId, keyword, reportContext, reportMonth, storeId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const totals = useMemo(() => data.staff.reduce((result, row) => {
    MONEY_FIELDS.forEach((field) => { result[field.apiKey] += Number(row[field.apiKey]) || 0; });
    return result;
  }, MONEY_FIELDS.reduce((result, field) => ({ ...result, [field.apiKey]: 0 }), {})), [data.staff]);

  const handleSave = async (formData) => {
    if (!editingRow) return;
    setSaving(true);
    try {
      const payload = MONEY_FIELDS.reduce((result, field) => ({
        ...result,
        [field.key]: parseMoney(formData[field.key]),
      }), {
        company_id: editingRow.companyid,
        business_id: editingRow.businessid,
        store_id: editingRow.storeid,
        report_month: reportMonth,
        note: formData.note?.trim() || null,
      });
      await updateEmployeeMonthlyIncome(editingRow.username, payload);
      showToast.success('Đã cập nhật thu nhập nhân viên.');
      setEditingRow(null);
      await loadReport();
    } catch (requestError) {
      showToast.error(requestError?.message || 'Không thể cập nhật thu nhập nhân viên.');
    } finally {
      setSaving(false);
    }
  };

  const exportExcel = () => {
    if (!canExport) return;
    const headers = [
      'STT', 'Mã NV', 'Tên nhân viên', 'Cửa hàng xuất', 'Chức danh', 'Phòng ban', 'Doanh thu VAT gồm VAT', 'Số đơn có hóa đơn',
      'Hoa hồng theo doanh số', 'Tip theo khai báo', 'Tive giao hàng', ...MONEY_FIELDS.map((field) => field.label), 'Tổng thu nhập', 'Ghi chú',
    ];
    const rows = data.staff.map((row, index) => [
      index + 1, row.username, row.fullname, row.storename, row.positionname, row.departmentname,
      Number(row.salesrevenue) || 0, Number(row.ordercount) || 0, Number(row.salescommission) || 0,
      Number(row.tipincome) || 0, Number(row.deliverycommission) || 0, ...MONEY_FIELDS.map((field) => Number(row[field.apiKey]) || 0),
      Number(row.totalincome) || 0, row.note || '',
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = headers.map((header, index) => ({ wch: index >= 5 && index < headers.length - 1 ? 18 : Math.max(12, Math.min(header.length + 4, 28)) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Thu nhập nhân viên');
    const orderRows = data.orders.map((order) => ({
      'Mã đơn': order.ordercode, 'Hóa đơn VAT': order.invoiceno || '', 'Ngày hóa đơn': formatDate(order.invoiceissueddate), 'Ngày đơn': formatDate(order.orderdate), 'Cửa hàng xuất': order.storename,
      'Nghiệp vụ': eventLabel(order), 'Ngày ghi nhận': formatDate(order.eventdate || order.orderdate),
      'Mã trả hàng': order.returncode || '', 'Giá trị hàng trả lại': Number(order.returnrevenue) || 0,
      'Thu nhập bị trừ do trả hàng': Number(order.returndeduction) || 0,
      'Hoa hồng đơn chuyển từ đặt trước': missingConvertedCommission(order) ? 'Chưa lưu hoa hồng' : '',
      'Mã NV': order.username, 'Tên nhân viên': order.fullname, 'Nhân viên cùng bán': order.sellernames || '',
      'Tiền hóa đơn gồm VAT': isVatInvoiceEvent(order) ? Number(order.orderrevenue) || 0 : 0,
      'Doanh thu VAT tại cửa hàng': isVatInvoiceEvent(order) ? Number(order.storeorderrevenue ?? order.orderrevenue) || 0 : 0,
      'Số nhân viên bán hàng': Number(order.salespersoncount) || 0,
      'Doanh thu VAT nhân viên': Number(order.salesrevenue) || 0, 'Hoa hồng theo doanh số': Number(order.salescommission) || 0,
      'Tip theo khai báo': Number(order.tipincome) || 0, 'Giao hàng': Number(order.deliverycommission) || 0,
      'Thu nhập setup': Number(order.setupincome) || 0,
    }));
    const orderSheet = XLSX.utils.json_to_sheet(orderRows);
    orderSheet['!cols'] = Array.from({ length: 20 }, () => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(workbook, orderSheet, 'Hóa đơn và thu nhập');
    const scopeSuffix = storeId ? `cua-hang-${selectedStore?.storecode || selectedStore?.storename || storeId}`
      : businessId ? `chi-nhanh-${selectedBusiness?.businessname || businessId}` : 'toan-bo-cua-hang';
    XLSX.writeFile(workbook, `thu-nhap-nhan-vien-${reportMonth}-${filenameScope(scopeSuffix)}.xlsx`);
  };

  const summary = data.summary || {};
  const hasCompany = Boolean(portalScope.company_id);
  const selectedOrders = ordersRow ? data.orders.filter((order) => sameEmployeeStore(order, ordersRow)) : [];

  return (
    <div className='bw_main_wrapp bc_emi_page'>
      <header className='bc_emi_page_header'>
        <div>
          <span className='bc_emi_eyebrow'>Báo cáo nhân sự theo tháng</span>
          <h2>Thu nhập và doanh thu VAT nhân viên</h2>
          <p>{scopeLabel}. Doanh thu gồm VAT lấy từ hóa đơn Viettel đã phát hành theo ngày hóa đơn và phân bổ cho nhân viên bán hàng. Hoa hồng, tip, setup và thu hồi thu nhập giữ theo nghiệp vụ trên đơn.</p>
        </div>
        <div className='bc_emi_header_actions'>
          {canOpenReport('SL_RETAIL_EMPLOYEE_PRODUCT_SALES_VIEW') ? <Link className='bc_emi_button is-secondary' to='/retail-reports/employee-product-sales'>Bán hàng theo nhân viên</Link> : null}
          {canOpenReport('SL_RETAIL_PRODUCT_SALES_VIEW') ? <Link className='bc_emi_button is-secondary' to='/retail-reports/product-sales'>Bán hàng theo hàng hóa</Link> : null}
          <button type='button' className='bc_emi_button is-secondary' title='Làm mới dữ liệu' onClick={loadReport} disabled={!hasCompany || loading}>
            <FiRefreshCw className={loading ? 'is-spinning' : ''} /> Làm mới
          </button>
          <button type='button' className='bc_emi_button is-primary' title='Xuất báo cáo Excel' onClick={exportExcel} disabled={!canExport}>
            <FiDownload /> Xuất Excel
          </button>
        </div>
      </header>

      <section className='bc_emi_filter_bar'>
        <label><span>Tháng báo cáo</span><input type='month' value={reportMonth} onChange={(event) => setReportMonth(event.target.value)} /></label>
        <label><span>Chi nhánh</span><select value={businessId} disabled={!hasCompany || !businessOptions.length} onChange={(event) => setScopeFilter({ companyId, businessId: event.target.value, storeId: '' })}>
          <option value=''>Tất cả chi nhánh</option>
          {businessOptions.map((row) => <option key={row.businessid} value={row.businessid}>{row.businessname || row.businessid}</option>)}
        </select></label>
        <label><span>Cửa hàng</span><select value={storeId} disabled={!hasCompany || !storeOptions.length} onChange={(event) => setScopeFilter({ companyId, businessId, storeId: event.target.value })}>
          <option value=''>Tất cả cửa hàng</option>
          {storeOptions.map((row) => <option key={row.storeid} value={row.storeid}>{row.storecode ? `${row.storecode} - ` : ''}{row.storename}</option>)}
        </select></label>
        <label className='bc_emi_keyword_filter'>
          <span>Tìm nhân viên</span>
          <div><FiSearch /><input value={keywordInput} placeholder='Mã, tên, chức danh hoặc phòng ban' onChange={(event) => setKeywordInput(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && setKeyword(keywordInput.trim())} /></div>
        </label>
        <button type='button' className='bc_emi_icon_button is-filter' title='Lọc báo cáo' aria-label='Lọc báo cáo' onClick={() => setKeyword(keywordInput.trim())}><FiSearch /></button>
      </section>

      {!hasCompany ? <section className='bc_emi_state'>Vui lòng chọn công ty trên thanh điều hướng để xem báo cáo.</section> : null}
      {error ? <section className='bc_emi_state is-error'>{error}</section> : null}
      {hasCompany && !error && Number(data.summary?.missingcommissionplancount) > 0 ? <section className='bc_emi_state' role='status'>
        Có {formatNumber(data.summary.missingcommissionplancount)} đơn chuyển từ đặt trước chưa lưu hoa hồng. Cần kiểm tra các đơn này trước khi chốt lương; bấm số đơn để xem chi tiết.
      </section> : null}

      {hasCompany && !error ? <>
        <section className='bc_emi_summary' aria-label='Tổng quan báo cáo'>
          <div><span>Nhân viên</span><strong>{formatNumber(summary.staffcount)}</strong><small>{formatNumber(summary.storecount ?? storeOptions.filter((row) => !storeId || `${row.storeid}` === storeId).length)} cửa hàng trong phạm vi báo cáo</small></div>
          <div><span>Doanh thu VAT (gồm VAT)</span><strong>{formatVatMoney(summary.salesrevenue)}</strong><small>{formatNumber(summary.ordercount)} đơn có hóa đơn VAT{Number(summary.invoicecount) > 0 ? `, ${formatNumber(summary.invoicecount)} hóa đơn` : ''}</small></div>
          <div role='group' aria-label='Thu nhập tự động'><span>Thu nhập tự động</span><strong>{formatMoney(summary.automaticincome ?? ((Number(summary.totalincome) || 0) - (Number(summary.manualincome) || 0)))}</strong><small>hoa hồng theo doanh số, tip và setup</small></div>
          <div><span>Khoản điều chỉnh</span><strong>{formatMoney(summary.manualincome)}</strong><small>kỹ thuật, phụ cấp, phát sinh</small></div>
          <div className='is-total'><span>Tổng thu nhập</span><strong>{formatMoney(summary.totalincome)}</strong><small>không bao gồm doanh thu VAT</small></div>
        </section>

        <section className='bc_emi_table_section'>
          <div className='bc_emi_table_heading'>
            <div><h3>Bảng tổng hợp tháng {reportMonth.split('-').reverse().join('/')}</h3><p>Mỗi dòng là một nhân viên tại một cửa hàng. Bấm số đơn để đối chiếu đơn bán và các khoản trả hàng.</p></div>
            <span>{loading ? 'Đang tổng hợp...' : `${formatNumber(data.staff.length)} dòng nhân viên / cửa hàng`}</span>
          </div>
          <div className='bc_emi_table_wrap'>
            <table className='bc_emi_table'>
              <thead><tr>
                <th className='is-sticky is-index'>STT</th>
                <th className='is-sticky is-code'>Mã NV</th>
                <th className='is-sticky is-name'>Tên nhân viên</th>
                <th>Cửa hàng xuất</th>
                <th>Chức danh</th><th>Phòng ban</th>
                <th className='is-sales-group'>Doanh thu VAT</th><th className='is-sales-group'>Số đơn có HĐ</th>
                <th className='is-sales-group' title='Hoa hồng theo doanh số'>HH doanh số</th><th className='is-sales-group'>Tip theo khai báo</th><th className='is-auto-group'>Giao hàng</th>
                {MONEY_FIELDS.map((field) => <th key={field.key} className={field.key === 'setup_income' ? 'is-auto-group' : ''} title={field.label}>{field.short}</th>)}
                <th>Tổng thu nhập</th><th>Ghi chú</th><th>Trạng thái</th><th>Thao tác</th>
              </tr></thead>
              <tbody>
                {!loading && !data.staff.length ? <tr><td colSpan={24} className='bc_emi_empty'>Không có nhân viên phù hợp bộ lọc.</td></tr> : null}
                {data.staff.map((row, index) => (
                  <tr key={rowIdentity(row)}>
                    <td className='is-sticky is-index'>{index + 1}</td>
                    <td className='is-sticky is-code'><strong>{row.username}</strong></td>
                    <td className='is-sticky is-name'><strong>{row.fullname}</strong></td>
                    <td className='is-store'>{row.storename}</td>
                    <td>{row.positionname}</td><td>{row.departmentname}</td>
                    <td className='is-money is-revenue'>{formatVatMoney(row.salesrevenue, true)}</td>
                    <td className='is-number'><button type='button' className='bc_emi_order_count' aria-label={`Xem đơn hàng ${row.username} tại ${row.storename}`} disabled={loading || (!Number(row.ordercount) && !data.orders.some((order) => sameEmployeeStore(order, row)))} onClick={() => setOrdersRow(row)}>{formatNumber(row.ordercount)}</button></td>
                    <td className='is-money'>{formatMoney(row.salescommission, true)}</td>
                    <td className='is-money'>{formatMoney(row.tipincome, true)}</td>
                    <td className='is-money'>{formatMoney(row.deliverycommission, true)}</td>
                    {MONEY_FIELDS.map((field) => <td key={field.key} className='is-money'>{formatMoney(row[field.apiKey], true)}</td>)}
                    <td className='is-money is-total'>{formatMoney(row.totalincome, true)}</td>
                    <td className='is-note' title={row.note || ''}>{row.note || '—'}</td>
                    <td><span className={`bc_emi_status ${row.hasmanualadjustment ? 'is-adjusted' : ''}`}>{row.hasmanualadjustment ? 'Đã bổ sung' : 'Tự động'}</span></td>
                    <td>
                      {canEdit ? <button type='button' className='bc_emi_icon_button is-edit' title={`Điều chỉnh ${row.fullname} tại ${row.storename}`} aria-label={`Điều chỉnh ${row.fullname} tại ${row.storename}`} disabled={loading || !row.companyid || !row.businessid || !row.storeid} onClick={() => setEditingRow(row)}><FiEdit3 /></button> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {data.staff.length ? <tfoot><tr>
                <td colSpan='6'>Tổng cộng</td>
                <td className='is-money'>{formatVatMoney(summary.salesrevenue)}</td><td className='is-number'>{formatNumber(summary.ordercount)}</td>
                <td className='is-money'>{formatMoney(summary.salescommission)}</td><td className='is-money'>{formatMoney(summary.tipincome)}</td><td className='is-money'>{formatMoney(summary.deliverycommission)}</td>
                {MONEY_FIELDS.map((field) => <td key={field.key} className='is-money'>{formatMoney(totals[field.apiKey])}</td>)}
                <td className='is-money is-total'>{formatMoney(summary.totalincome)}</td><td colSpan='3' />
              </tr></tfoot> : null}
            </table>
          </div>
        </section>
      </> : null}

      <EmployeeIncomeModal row={hasCurrentReport ? editingRow : null} open={hasCurrentReport && Boolean(editingRow)} saving={saving} onClose={() => !saving && setEditingRow(null)} onSave={handleSave} />
      <EmployeeOrdersModal row={hasCurrentReport ? ordersRow : null} orders={selectedOrders} onClose={() => setOrdersRow(null)} />
    </div>
  );
};

export default EmployeeMonthlyIncomePage;
